import { useQuery } from '@tanstack/react-query';
import { scopeLabel } from '@/pages/registration';
import { backend } from '@/api/backend';
import { ApiError } from '@/api/http';
import { useBucketToolDetections } from '@/api/hooks';
import { FAMILIES_REQUIRED, SIGNALS_REQUIRED } from '@/components/PromoteDetection';
import type { WorkflowFacts } from '@/pages/workflow';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/**
 * Where one bucket stands, read once and shared.
 *
 * The bucket page assembled these facts inline, which was fine while it was the
 * only surface drawing the spine. The rail draws it too now - the six steps are
 * the product's navigation, not decoration on one page - and two places
 * deriving the same position from the same reads is how they end up disagreeing.
 *
 * Every query here is keyed exactly as the pages key theirs, so the rail costs
 * no extra request: React Query serves both from one cache entry.
 */
export function useScopeFacts(bucketId: string | undefined, companyId: string | undefined) {
  const bucket = useQuery({
    queryKey: ['flow-bucket', bucketId],
    queryFn: ({ signal }) => backend.getBucket(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
  });
  const company = companyId ?? bucket.data?.bucket.companyId;

  const syncs = useQuery({
    queryKey: ['sync-runs', bucketId],
    queryFn: ({ signal }) => backend.listSyncRuns(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    staleTime: 15_000,
  });
  const toolset = useQuery({
    queryKey: ['toolset', bucketId],
    queryFn: ({ signal }) => backend.getBucketToolset(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    staleTime: 15_000,
  });
  const packaging = useQuery({
    queryKey: ['packaging-runs', bucketId],
    queryFn: ({ signal }) => backend.listPackagingRuns(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    staleTime: 15_000,
  });
  // This bucket's detections, from the route that returns only this bucket's.
  // It used to read the company's whole inventory and filter here, which is
  // the correct answer computed from data that should never have arrived.
  const tools = useBucketToolDetections(bucketId);

  const latestSync = syncs.data?.items[0];
  const activeSync = syncs.data?.items.find(
    (r) => r.state !== 'COMPLETE' && r.state !== 'FAILED' && r.state !== 'ACCESS_BLOCKED',
  );
  const mine = tools.data?.items ?? [];
  /** Null, not zero, when the detections read did not answer. */
  const known = tools.isSuccess;
  const runs =
    ((packaging.data as { items?: { state: string; supersededBy?: unknown }[] } | undefined)?.items ?? []).find(
      (r) => !r.supersededBy,
    ) ?? null;

  const facts: WorkflowFacts = {
    hasContext: Boolean(bucketId),
    known: toolset.isSuccess || toolset.isError,
    observed: latestSync?.state === 'COMPLETE',
    observing: Boolean(activeSync),
    sealed: Boolean(toolset.data?.sealedAt),
    walking: false,
    toolCount: toolset.data?.entries.length ?? 0,
    // The seal's own count of what it covered, and the listing's count of what
    // is there. A seal over zero is a failed walk, not a finished one.
    sealedObjects: toolset.data?.conservation?.inventoryObjectCount ?? null,
    listedObjects: latestSync?.state === 'COMPLETE' ? (latestSync.listed ?? null) : null,
    promotable: known
      ? mine.filter(
          (t) =>
            t.status === 'CANDIDATE' &&
            t.evidence.signals >= SIGNALS_REQUIRED &&
            t.evidence.families >= FAMILIES_REQUIRED,
        ).length
      : null,
    supported: known ? mine.filter((t) => t.status === 'EVIDENCE_BACKED').length : null,
    packagingState: runs?.state ?? null,
    exportsBuilt: false,
    populationRegistered: null,
    deliveryState: null,
  };

  return {
    facts,
    // The SCOPE's name, not the bucket's. Two scopes over one bucket arrive
    // carrying the same bucket name, and drawing both as `capway` is how
    // someone concludes they are looking at everything.
    bucketName: bucket.data
      ? scopeLabel(bucket.data.bucket.name, (bucket.data as { prefix?: string }).prefix ?? '')
      : undefined,
    /** The prefix, so the rail can say folder or bucket rather than assume. */
    prefix: bucket.data ? ((bucket.data as { prefix?: string }).prefix ?? '') : '',
    /** So the rail can hold a skeleton rather than guess a name. */
    naming: bucket.isPending,
    companyId: company,
    sealed: facts.sealed,
  };
}
